import { MonoLabel, costValueColor } from '@/design-system';
import type { PublicLanguage, PublicNhg } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { formatAmount } from '../format';
import { PublicBlock } from './PublicBlock';
import { SectionTitle } from './SectionTitle';

export interface NhgBlockProps {
    nhg: PublicNhg | null;
    language: PublicLanguage;
    copy: PublicCopy;
    headingLevel: 2 | 3;
}

/**
 * De NHG-bijlage voor de koper en de hypotheekadviseur: per onderdeel wat direct en
 * op termijn noodzakelijk is, met de hoofdstukken van het rapport waar het staat en
 * de totalen, zoals in de PDF. Alleen de regels met kosten: wie het hele formulier
 * wil, opent de PDF.
 *
 * Zonder bijlage, of met verborgen kosten, verdwijnt het blok helemaal. Op een
 * telefoon schuift de tabel binnen het blok; hij wordt nooit afgekapt.
 */
export function NhgBlock({ nhg, language, copy, headingLevel }: NhgBlockProps) {
    if (nhg === null) {
        return null;
    }

    const rows = nhg.tables.flatMap((table) => table.rows);
    const money = (amount: number) => (amount === 0 ? '—' : formatAmount(amount, language));

    return (
        <PublicBlock>
            <SectionTitle level={headingLevel} description={copy.nhgSubtitle}>
                {copy.nhgTitle}
            </SectionTitle>

            {rows.length === 0 ? (
                <p className="m-0 text-[14px] leading-[1.5] text-[var(--text-secondary)]">
                    {copy.nhgEmpty}
                </p>
            ) : (
                <div className="overflow-x-auto rounded-[10px] border border-[var(--border-default)]">
                    <table
                        aria-label={copy.nhgTitle}
                        className="w-full min-w-[520px] border-collapse font-[family-name:var(--font-body)] text-[14px] text-[var(--text-primary)]"
                    >
                        <thead className="bg-[var(--surface-subtle)]">
                            <tr>
                                {[
                                    copy.nhgColumns.code,
                                    copy.nhgColumns.element,
                                    copy.nhgColumns.locations,
                                ].map((label) => (
                                    <th
                                        key={label}
                                        scope="col"
                                        className="px-[14px] py-[10px] text-left text-[12px] font-medium text-[var(--text-tertiary-safe)]"
                                    >
                                        {label}
                                    </th>
                                ))}
                                {[copy.nhgColumns.direct, copy.nhgColumns.termijn].map((label) => (
                                    <th
                                        key={label}
                                        scope="col"
                                        className="px-[14px] py-[10px] text-right text-[12px] font-medium text-[var(--text-tertiary-safe)]"
                                    >
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr
                                    key={row.code}
                                    className="border-t border-[var(--border-subtle-2)]"
                                >
                                    <td className="px-[14px] py-[12px] align-top">
                                        <MonoLabel size={11} variant="value" tone="muted">
                                            {row.code}
                                        </MonoLabel>
                                    </td>
                                    <td className="px-[14px] py-[12px] align-top font-medium">
                                        {row.element}
                                    </td>
                                    <td className="px-[14px] py-[12px] align-top text-[13px] text-[var(--text-secondary)]">
                                        {row.locations.join(', ')}
                                    </td>
                                    <td
                                        className="px-[14px] py-[12px] text-right align-top font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap"
                                        style={{
                                            color:
                                                row.direct === 0
                                                    ? undefined
                                                    : costValueColor.direct,
                                        }}
                                    >
                                        {money(row.direct)}
                                    </td>
                                    <td
                                        className="px-[14px] py-[12px] text-right align-top font-[family-name:var(--font-mono)] text-[13px] whitespace-nowrap"
                                        style={{
                                            color:
                                                row.termijn === 0
                                                    ? undefined
                                                    : costValueColor.termijn,
                                        }}
                                    >
                                        {money(row.termijn)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="border-t border-[var(--border-default)] bg-[var(--surface-subtle)]">
                                <th
                                    scope="row"
                                    colSpan={3}
                                    className="px-[14px] py-[12px] text-left text-[14px] font-medium"
                                >
                                    {copy.nhgTotal}
                                </th>
                                <td className="px-[14px] py-[12px] text-right font-[family-name:var(--font-mono)] text-[13px] font-medium whitespace-nowrap">
                                    {formatAmount(nhg.total.direct, language)}
                                </td>
                                <td className="px-[14px] py-[12px] text-right font-[family-name:var(--font-mono)] text-[13px] font-medium whitespace-nowrap">
                                    {formatAmount(nhg.total.termijn, language)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            <div className="mt-[12px] grid gap-[6px] text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                {nhg.improvement > 0 ? (
                    <p className="m-0 flex flex-wrap justify-between gap-[10px]">
                        <span>{copy.nhgImprovement}</span>
                        <span className="font-[family-name:var(--font-mono)] text-[var(--text-primary)]">
                            {formatAmount(nhg.improvement, language)}
                        </span>
                    </p>
                ) : null}
                {nhg.outside > 0 ? (
                    <p className="m-0 text-[var(--text-tertiary-safe)]">
                        {copy.nhgOutside(formatAmount(nhg.outside, language))}
                    </p>
                ) : null}
            </div>
        </PublicBlock>
    );
}
